import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import {
	Box,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Paper,
	Typography,
} from '@mui/material'
import mermaid from 'mermaid'
import { useEffect, useRef, useState } from 'react'

import { useAppContext } from '@/hooks'
import { logError } from '@/utils/errorHandling'

interface AiMermaidBlockProps {
	code: string
	streaming?: boolean
}

// Initialize mermaid with default config
mermaid.initialize({
	startOnLoad: false,
	theme: 'default',
	securityLevel: 'loose',
	fontFamily: 'inherit',
})

export const AiMermaidBlock = ({ code, streaming }: AiMermaidBlockProps) => {
	const { t } = useAppContext()
	const tRef = useRef(t)
	tRef.current = t

	const [svg, setSvg] = useState<string>('')
	const [error, setError] = useState<string>('')
	const [copied, setCopied] = useState(false)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const diagramIdRef = useRef(`mermaid-${Math.random().toString(36).substring(2, 11)}`)

	useEffect(() => {
		if (streaming) {
			return
		}

		const renderDiagram = async () => {
			try {
				setError('')
				const { svg: renderedSvg } = await mermaid.render(
					diagramIdRef.current,
					code,
				)
				setSvg(renderedSvg)
			} catch (err) {
				logError(err, { operation: 'AiMermaidBlock.renderDiagram' })
				setError(
					err instanceof Error ? err.message : tRef.current('MERMAID_RENDER_FAILED'),
				)
			}
		}

		renderDiagram()
	}, [code, streaming])

	const handleCopy = () => {
		navigator.clipboard.writeText(code)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	const handleOpenModal = () => {
		setIsModalOpen(true)
	}

	const handleCloseModal = () => {
		setIsModalOpen(false)
	}

	// While streaming, show the raw code
	if (streaming) {
		return (
			<Box sx={{ my: 2 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						mb: 1,
					}}
				>
					<Typography variant="caption" sx={{ color: 'text.secondary' }}>
						{t('MERMAID_STREAMING')}
					</Typography>
				</Box>
				<Paper
					elevation={0}
					sx={{
						p: 2,
						bgcolor: '#2d2d2d',
						color: '#f8f8f2',
						borderRadius: 1,
						border: '1px solid',
						borderColor: 'grey.300',
						fontFamily: 'monospace',
						fontSize: '0.875rem',
						whiteSpace: 'pre-wrap',
						wordBreak: 'break-word',
						overflowX: 'auto',
					}}
				>
					{code}
				</Paper>
			</Box>
		)
	}

	// If there's an error, show it
	if (error) {
		return (
			<Box sx={{ my: 2 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						mb: 1,
					}}
				>
					<Typography variant="caption" sx={{ color: 'error.main' }}>
						{t('MERMAID_RENDER_ERROR')}
					</Typography>
					<IconButton
						size="small"
						onClick={handleCopy}
						title={copied ? t('COPIED') : t('COPY_TO_CLIPBOARD')}
						sx={{
							p: 0.5,
							color: copied ? 'success.main' : 'text.secondary',
							'&:hover': {
								color: copied ? 'success.main' : 'primary.main',
							},
						}}
					>
						<ContentCopyIcon fontSize="small" />
					</IconButton>
				</Box>
				<Paper
					elevation={0}
					sx={{
						p: 2,
						bgcolor: '#fff3cd',
						color: '#856404',
						borderRadius: 1,
						border: '1px solid #ffeaa7',
						fontSize: '0.875rem',
					}}
				>
					<Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
						{t('ERROR_RENDERING_MERMAID_DIAGRAM')}
					</Typography>
					<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
						{error}
					</Typography>
				</Paper>
			</Box>
		)
	}

	// Successfully rendered diagram
	return (
		<>
			<Box sx={{ my: 2 }}>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						mb: 1,
					}}
				>
					<Typography variant="caption" sx={{ color: 'text.secondary' }}>
						mermaid
					</Typography>
					<Box sx={{ display: 'flex', gap: 0.5 }}>
						<IconButton
							size="small"
							onClick={handleOpenModal}
							title={t('ENLARGE_DIAGRAM')}
							sx={{
								p: 0.5,
								color: 'text.secondary',
								'&:hover': {
									color: 'primary.main',
								},
							}}
						>
							<ZoomInIcon fontSize="small" />
						</IconButton>
						<IconButton
							size="small"
							onClick={handleCopy}
							title={copied ? t('COPIED') : t('COPY_CODE_TO_CLIPBOARD')}
							sx={{
								p: 0.5,
								color: copied ? 'success.main' : 'text.secondary',
								'&:hover': {
									color: copied ? 'success.main' : 'primary.main',
								},
							}}
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
					</Box>
				</Box>
				<Paper
					ref={containerRef}
					elevation={0}
					sx={{
						p: 2,
						bgcolor: 'background.paper',
						borderRadius: 1,
						border: '1px solid',
						borderColor: 'grey.300',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						cursor: 'pointer',
						transition: 'box-shadow 0.2s',
						'&:hover': {
							boxShadow: 2,
						},
						maxWidth: '100%',
						overflow: 'auto',
					}}
					onClick={handleOpenModal}
				>
					<Box
						dangerouslySetInnerHTML={{ __html: svg }}
						sx={{
							maxWidth: '100%',
							'& svg': {
								maxWidth: '100%',
								height: 'auto',
							},
						}}
					/>
				</Paper>
			</Box>

			{/* Enlargement Modal */}
			<Dialog
				open={isModalOpen}
				onClose={handleCloseModal}
				maxWidth={false}
				PaperProps={{
					sx: {
						width: '80vw',
						height: '80vh',
						maxWidth: '80vw',
						maxHeight: '80vh',
					},
				}}
			>
				<DialogTitle
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<Typography variant="h6">{t('MERMAID_DIAGRAM')}</Typography>
					<Box sx={{ display: 'flex', gap: 1 }}>
						<IconButton
							size="small"
							onClick={handleCopy}
							title={copied ? t('COPIED') : t('COPY_CODE_TO_CLIPBOARD')}
							sx={{
								color: copied ? 'success.main' : 'text.secondary',
							}}
						>
							<ContentCopyIcon fontSize="small" />
						</IconButton>
						<IconButton size="small" onClick={handleCloseModal}>
							<CloseIcon />
						</IconButton>
					</Box>
				</DialogTitle>
				<DialogContent
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						p: 3,
						overflow: 'auto',
						height: '100%',
					}}
				>
					<Box
						dangerouslySetInnerHTML={{ __html: svg }}
						sx={{
							width: '100%',
							height: '100%',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							'& svg': {
								width: 'auto !important',
								height: 'auto !important',
								maxWidth: '100%',
								maxHeight: '100%',
								minWidth: '50%',
								minHeight: '50%',
							},
						}}
					/>
				</DialogContent>
			</Dialog>
		</>
	)
}
